import { useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { apiError } from "../../lib/auth";
import { NeonButton } from "../common";
import { CopyBlock, Do } from "./Bits";

const Secret = ({ field, placeholder, testid, onSaved }) => {
  const [v, setV] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/connect/whop", { [field]: v }); setV(""); onSaved(data);
      const k = data.steps.find((s) => s.id === "whop_key").checks[0];
      if (field === "api_key" && !k.ok) toast.error(`Saved, but ${k.detail}`); else toast.success("Saved and encrypted");
    }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="flex gap-2 max-w-xl">
      <input type="password" autoComplete="off" value={v} onChange={(e) => setV(e.target.value)} placeholder={placeholder}
        className="flex-1 bg-black/60 border border-white/15 px-3 py-2 font-mono text-xs focus:border-brand outline-none" data-testid={`${testid}-input`} />
      <NeonButton onClick={save} disabled={!v.trim() || busy} data-testid={`${testid}-save`}>{busy ? "Checking…" : "Save"}</NeonButton>
    </div>
  );
};

export const WhopKey = ({ edit, onSaved, reload }) => {
  const test = async () => {
    try { const { data } = await api.post("/connect/whop/test"); toast[data.ok ? "success" : "error"](data.detail); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return <>
    <Do>
      <li>In Whop open <b>Dashboard → Developer → API keys</b> and create a <b>Company API key</b>.</li>
      <li>Give it permission for <b>payments, memberships, disputes, resolution center</b> and <b>developer: manage webhooks</b> (that last one lets AEGIS set up step 2 for you).</li>
      <li>Paste it below. AEGIS encrypts it, tests it with Whop and creates your webhook automatically. Never put this key in a web page.</li>
    </Do>
    {edit && <div className="flex flex-wrap gap-2 items-center"><Secret field="api_key" placeholder="Whop API key" testid="whop-key" onSaved={onSaved} />
      <NeonButton variant="ghost" onClick={test} data-testid="whop-key-test">Test connection</NeonButton></div>}
  </>;
};

export const Webhook = ({ v, edit, onSaved }) => {
  const [busy, setBusy] = useState(false);
  const auto = async () => {
    setBusy(true);
    try { const { data } = await api.post("/connect/whop/auto-webhook"); toast[data.ok ? "success" : "error"](data.detail); onSaved(data); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return <>
    <p className="text-sm text-white/70">AEGIS creates the webhook inside your Whop account for you, picks the right events, stores the signing secret encrypted and switches off any old AEGIS webhook.</p>
    {edit && <NeonButton onClick={auto} disabled={busy} data-testid="webhook-auto-btn">{busy ? "Setting up…" : "Set up automatically"}</NeonButton>}
    <details data-testid="webhook-manual">
      <summary className="cursor-pointer font-mono text-[11px] text-white/50 hover:text-white">Prefer to do it by hand? Manual steps</summary>
      <div className="space-y-3 mt-3">
        <Do>
          <li>In Whop open <b>Dashboard → Developer → Webhooks → Create webhook</b>.</li>
          <li>Paste this exact URL. Remove any old AEGIS webhook that points somewhere else.</li>
        </Do>
        <CopyBlock text={v.webhook_url} testid="copy-webhook-url" label="Webhook URL" />
        <Do start={3}><li>Tick <b>all {v.events.length}</b> of these events (Whop groups them by type):</li></Do>
        <CopyBlock text={v.events.join("\n")} testid="copy-webhook-events" label="Event list" />
        <Do start={4}><li>Save it in Whop, copy the <b>signing secret</b> Whop shows, and paste it here.</li></Do>
        {edit && <Secret field="webhook_secret" placeholder="Signing secret (ws_…)" testid="whop-secret" onSaved={onSaved} />}
      </div>
    </details>
    <p className="text-xs text-white/45">The second check turns green the moment Whop delivers a real event (for example your test purchase in the last step).</p>
  </>;
};

export const Resolution = ({ edit, reload }) => {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { const { data } = await api.post("/resolution/import"); toast.success(`Imported ${data.imported ?? data.count ?? 0} case(s)`); reload(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return <>
    <p className="text-sm text-white/70">Pulls in any open Resolution Center cases once. After this, new cases arrive automatically through the webhook from step 2.</p>
    {edit && <NeonButton onClick={run} disabled={busy} data-testid="reso-import-btn">{busy ? "Importing…" : "Import cases"}</NeonButton>}
  </>;
};

export const Origins = ({ v, edit, reload }) => {
  const [text, setText] = useState(v.embed_origins.join("\n"));
  const save = async () => {
    const list = text.split(/[\s,]+/).map((x) => x.trim().replace(/\/$/, "")).filter(Boolean);
    if (list.some((o) => !/^https:\/\/[^/]+$/.test(o))) return toast.error("Each entry must look like https://www.your-site.com (no path)");
    try { const { data: cfg } = await api.get("/verify/config"); await api.put("/verify/config", { ...cfg, embed_origins: list }); toast.success("Allowed websites saved"); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return <>
    <p className="text-sm text-white/70">List every https address of the page(s) where buyers will see the verification step: one per line, no paths. AEGIS only sends its "finished" message to these addresses.</p>
    {edit ? <>
      <textarea rows={4} value={text} onChange={(e) => setText(e.target.value)} placeholder="https://www.your-site.com" className="w-full max-w-xl bg-black/60 border border-white/15 p-3 font-mono text-xs focus:border-brand outline-none" data-testid="origins-input" />
      <NeonButton onClick={save} data-testid="origins-save">Save allowed websites</NeonButton>
    </> : <CopyBlock text={v.embed_origins.join("\n") || "(none yet)"} testid="copy-origins" label="Origins" />}
  </>;
};

const NextPage = ({ v, edit, reload }) => {
  const [url, setUrl] = useState(v.redirect_url || "");
  const save = async () => {
    if (!/^https:\/\/\S+$/.test(url.trim())) return toast.error("Use a full https:// address");
    try { const { data: cfg } = await api.get("/verify/config"); await api.put("/verify/config", { ...cfg, redirect_url: url.trim() }); toast.success("Next page saved"); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };
  if (!edit) return <p className="text-sm text-white/70">After the video, buyers go to: <code className="text-brand">{v.redirect_url || "(not set)"}</code></p>;
  return <div className="flex gap-2 max-w-xl">
    <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://your-site.com/next-step" className="flex-1 bg-black/60 border border-white/15 px-3 py-2 font-mono text-xs focus:border-brand outline-none" data-testid="next-page-input" />
    <NeonButton onClick={save} data-testid="next-page-save">Save</NeonButton>
  </div>;
};

const MODES = [["link", "No-code link (recommended)"], ["embed", "Embed on my site"], ["secure", "Secure session (developers)"]];

export const Embed = ({ v, edit, reload }) => {
  const [mode, setMode] = useState("link");
  return <>
    <div className="flex flex-wrap border border-white/15 w-fit" data-testid="embed-mode">
      {MODES.map(([k, l]) => (
        <button key={k} onClick={() => setMode(k)} className={`px-3 py-2 font-mono text-[11px] ${mode === k ? "bg-brand text-black" : "text-white/60 hover:text-white"}`} data-testid={`embed-mode-${k}`}>{l}</button>
      ))}
    </div>
    {mode === "link" && <>
      <Do><li>Copy your verification link:</li></Do>
      <CopyBlock text={v.verify_url} testid="copy-verify-link" label="Verification link" />
      <Do start={2}>
        <li>In Whop go to <b>Dashboard → Products</b>, open the product (or the upsell) buyers purchase, and paste the link into its <b>redirect after purchase / next page</b> setting (Whop labels it slightly differently on checkout links and upsells).</li>
        <li>Choose where buyers go after their video (for example your waitlist or booking page):</li>
      </Do>
      <NextPage v={v} edit={edit} reload={reload} />
      <p className="text-xs text-white/50">No code needed. If Whop doesn't pass the buyer's details, the page asks for their checkout email so the video still links to the right payment.</p>
    </>}
    {mode === "embed" && <>
      <Do><li>Paste this where the verification should appear. Your platform fills in the <code>{"{{placeholders}}"}</code>; leave out any it can't provide. Add your site in the next step.</li></Do>
      <CopyBlock text={v.iframe} testid="copy-iframe" label="Iframe" />
      <Do start={2}><li>Add this listener once on the same page. It sends the buyer on when they finish or skip:</li></Do>
      <CopyBlock text={v.listener} testid="copy-listener" label="Listener" />
      <p className="text-xs text-white/55">Pin message origin to exactly <code className="text-brand" data-testid="message-origin">{v.message_origin}</code>. Keep a timeout fallback so nobody is ever blocked.</p>
    </>}
    {mode === "secure" && <>
      <Do><li>Create the key in the "Secure one-time sessions" step, store it on your server, and add this server route:</li></Do>
      <CopyBlock text={v.session_server} testid="copy-session-server" label="Server code" />
      <Do start={2}><li>Then use this on the page instead of the simple iframe, plus the listener from "Embed on my site":</li></Do>
      <CopyBlock text={v.session_page} testid="copy-session-page" label="Page code" />
    </>}
  </>;
};

export const Domain = ({ v, edit, reload }) => {
  const [d, setD] = useState(v.custom_domain || "");
  const save = async () => {
    try { const { data: cfg } = await api.get("/verify/config"); await api.put("/verify/config", { ...cfg, custom_domain: d.trim() }); setD(d.trim().toLowerCase().replace(/^https?:\/\//, "").replace(/\/+$/, "")); toast.success(d.trim() ? "Saved. We'll send you the DNS record to add" : "Removed. Using the shared address"); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return <>
    <p className="text-sm text-white/70">Phones show the web address in the camera prompt. Use an address on your own domain so buyers see your brand, not ours.</p>
    <Do>
      <li>Pick an address on your domain, for example <code>verify.your-brand.com</code>, and save it below.</li>
      <li>We connect it on our side and send you <b>one DNS record</b> to add at your domain provider (GoDaddy, Namecheap, Cloudflare…).</li>
      <li>Press <b>Run live checks</b>. When it turns green, every link and code snippet on this page switches to your address automatically. Update the Whop redirect link once.</li>
    </Do>
    {edit && <div className="flex gap-2 max-w-xl">
      <input value={d} onChange={(e) => setD(e.target.value)} placeholder="verify.your-brand.com" className="flex-1 bg-black/60 border border-white/15 px-3 py-2 font-mono text-xs focus:border-brand outline-none" data-testid="domain-input" />
      <NeonButton onClick={save} data-testid="domain-save">Save</NeonButton>
    </div>}
  </>;
};

export const Session = ({ edit, reload }) => {
  const [key, setKey] = useState(null);
  const make = async () => {
    try { const { data } = await api.post("/hub/keys", { name: "Verification sessions", platform: "custom", scopes: ["verify:session"] }); setKey(data.key); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return <>
    <p className="text-sm text-white/70">Only for stores with their own server (the "Secure session" option). Your server swaps this key for a one-time link that works once and expires in 15 minutes, so nothing about the buyer is in the page URL.</p>
    {edit && !key && <NeonButton onClick={make} data-testid="session-key-create">Create session key</NeonButton>}
    {key && <><p className="text-xs text-amber-300">Copy it now: it's shown only once. Store it as AEGIS_KEY on your server, never in the page.</p><CopyBlock text={key} testid="copy-session-key" label="Key" /></>}
  </>;
};

export const TestRun = () => (
  <Do>
    <li>On your phone, buy your own product through the normal Whop checkout.</li>
    <li>Continue to the verification step, allow camera and microphone, and record the 10-second video.</li>
    <li>Come back here and press <b>Run live checks</b>. All three checks should be green: payment received, video recorded, video linked to the payment.</li>
  </Do>
);
